import * as React from 'react'
import {observer} from 'mobx-react-lite'
import {Avatar, Badge} from '@mui/material'
import {makeOcStyles, syskronPalette} from '@oc/styles'
import {DataCyProps} from '@oc/components'
import {EditIcon} from '@oc/icons'
import {User} from '@oc/user-management'
import {useOcAppBarState} from '../../Contexts/OcAppBarStateContext'

interface ProfilePictureButtonProps extends DataCyProps {
    onClick: (e: React.MouseEvent) => void
}

interface ProfilePictureEditButtonProps extends DataCyProps {
    closeMenu: () => void
}

interface ProfileAvatarProps extends DataCyProps {
    disabled?: boolean
    onClick: (e: React.MouseEvent) => void
    pictureUrl?: string
    displayText: string
    size: 'big' | 'small'
}

const useStyles = makeOcStyles(theme => {
    const smallSize = theme.spacing(4)
    const bigSize = theme.spacing(8)

    return {
        smallSize: {
            width: smallSize,
            height: smallSize,
            fontSize: '1rem'
        },
        bigSize: {
            width: bigSize,
            height: bigSize
        },
        color: {
            backgroundColor: theme.syskronPalette.colors.P300,
            color: theme.syskronPalette.colors.P700
        }
    }
})

function getUserInicials(user?: User) {
    const firstName = user?.givenName
    const lastName = user?.familyName
    const displayText = `${firstName?.charAt(0) || '-'}${lastName?.charAt(0) || '-'}`.toUpperCase()

    return displayText
}

function _ProfileAvatar(props: ProfileAvatarProps) {
    const {displayText, onClick, disabled, pictureUrl, 'data-cy': dataCy, size} = props
    const {classes, cx} = useStyles()

    if (pictureUrl) {
        return (
            <Avatar
                className={cx(classes.color, size === 'big' ? classes.bigSize : classes.smallSize)}
                onClick={disabled ? undefined : onClick}
                src={pictureUrl}
                data-cy={dataCy}
            />
        )
    }
    return (
        <Avatar
            className={cx(classes.color, size === 'big' ? classes.bigSize : classes.smallSize)}
            onClick={disabled ? undefined : onClick}
            data-cy={dataCy}
        >
            {displayText}
        </Avatar>
    )
}

const ProfileAvatar = observer(_ProfileAvatar)

function _ProfilePictureButton(props: ProfilePictureButtonProps) {
    const {onClick, 'data-cy': dataCy} = props
    const state = useOcAppBarState()
    const disabled = !state.loggedUser

    const displayText = getUserInicials(state.loggedUser)
    const pictureUrl = state.loggedUser?.pictureUrl

    return (
        <ProfileAvatar
            displayText={displayText}
            onClick={onClick}
            data-cy={dataCy}
            disabled={disabled}
            pictureUrl={pictureUrl}
            size="small"
        />
    )
}

export const ProfilePictureButton = observer(_ProfilePictureButton)

function _ProfilePictureEditButton(props: ProfilePictureEditButtonProps) {
    const {closeMenu, 'data-cy': dataCy} = props
    const state = useOcAppBarState()
    const disabled = !state.loggedUser || !state.userInfoLoaded

    const displayText = getUserInicials(state.loggedUser)
    const pictureUrl = state.loggedUser?.pictureUrl

    const onClick = React.useCallback(() => {
        closeMenu()
        state.toggleFileUploadDialog(true)
    }, [state, closeMenu])

    return (
        <Badge
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right'
            }}
            badgeContent={<EditIcon fontSize="inherit" htmlColor={syskronPalette.colors.S500} />}
        >
            <ProfileAvatar
                displayText={displayText}
                onClick={onClick}
                data-cy={dataCy}
                disabled={disabled}
                pictureUrl={pictureUrl}
                size="big"
            />
        </Badge>
    )
}

export const ProfilePictureEditButton = observer(_ProfilePictureEditButton)
